import type { Car, CarStations } from '@/entities/cars/car.entity';
import type { MinimalAllocationDto } from '@/entities/allocations/allocation.entity';
import { DateTime } from 'luxon';
import type { HideInterval } from './components/interfaces';

const isPrimary = (s: { primary?: boolean }) => s.primary !== false;

const toInterval = (fromDate: string | null, untilDate: string | null): HideInterval => ({
  hideFrom: fromDate ? DateTime.fromISO(fromDate) : null,
  hideUntil: untilDate ? DateTime.fromISO(untilDate) : null,
});

const covers = (from: DateTime | null, until: DateTime | null, date: DateTime): boolean => {
  if (from && date < from.startOf('day')) return false;
  if (until && date > until.endOf('day')) return false;
  return true;
};

/** Intervals when the car is linked to stationId (primary or additional). */
export const linkedIntervalsForStation = (car: Car, stationId: string): HideInterval[] =>
  car.stations
    .filter((s) => s.station.id === stationId)
    .map((s) => toInterval(s.fromDate, s.untilDate));

/**
 * Hide the line when the car is affiliated elsewhere and not also linked here
 * (via additional station coverage).
 */
export const hideIntervalsForStationLine = (car: Car, stationId: string): HideInterval[] => {
  const otherPrimary = car.stations
    .filter((s) => isPrimary(s) && s.station.id !== stationId)
    .map((s) => toInterval(s.fromDate, s.untilDate));
  const linkedHere = linkedIntervalsForStation(car, stationId);
  return subtractIntervals(otherPrimary, linkedHere);
};

const intervalStart = (i: HideInterval) => i.hideFrom?.toMillis() ?? Number.NEGATIVE_INFINITY;
const intervalEnd = (i: HideInterval) => i.hideUntil?.toMillis() ?? Number.POSITIVE_INFINITY;

/** Subtract `subtrahends` from `minuends` (half-open-ish date ranges). */
export const subtractIntervals = (
  minuends: HideInterval[],
  subtrahends: HideInterval[],
): HideInterval[] => {
  let result = [...minuends];
  for (const sub of subtrahends) {
    const next: HideInterval[] = [];
    for (const m of result) {
      const mStart = intervalStart(m);
      const mEnd = intervalEnd(m);
      const sStart = intervalStart(sub);
      const sEnd = intervalEnd(sub);
      if (sEnd < mStart || sStart > mEnd) {
        next.push(m);
        continue;
      }
      // left remnant
      if (mStart < sStart) {
        next.push({
          hideFrom: m.hideFrom,
          hideUntil: sub.hideFrom,
        });
      }
      // right remnant
      if (mEnd > sEnd) {
        next.push({
          hideFrom: sub.hideUntil,
          hideUntil: m.hideUntil,
        });
      }
    }
    result = next;
  }
  return result;
};

const isAdditionalStationOnDate = (car: Car, stationId: string, date: Date | string): boolean => {
  const d = DateTime.fromJSDate(new Date(date));
  return car.stations.some(
    (s) =>
      s.primary === false &&
      s.station.id === stationId &&
      covers(
        s.fromDate ? DateTime.fromISO(s.fromDate) : null,
        s.untilDate ? DateTime.fromISO(s.untilDate) : null,
        d,
      ),
  );
};

/** Solid block: additional-station pickup, otherwise affiliation home. */
export const getAllocationPlacementStationId = (
  allocation: MinimalAllocationDto,
  car: Car | undefined,
): string => {
  const pickupId = allocation.pickupResponsibleStationId;
  if (pickupId && car && isAdditionalStationOnDate(car, pickupId, allocation.startDate)) {
    return pickupId;
  }
  return allocation.stationId;
};

export const isFloatingBooking = (
  allocation: Pick<MinimalAllocationDto, 'type' | 'bookingStatus'>,
) => allocation.type === 'BOOKING' && allocation.bookingStatus === 'FLOATING';

/** Other linked station ranges overlapping the allocation (for booked-at mirrors). */
export const getMirrorStationRanges = (
  allocation: MinimalAllocationDto,
  car: Car,
  placementStationId: string,
): { stationId: string; startDate: DateTime; endDate: DateTime }[] => {
  const start = DateTime.fromISO(allocation.startDate);
  const end = DateTime.fromISO(allocation.endDate);
  const ranges: { stationId: string; startDate: DateTime; endDate: DateTime }[] = [];
  for (const row of car.stations) {
    if (row.station.id === placementStationId) continue;
    const from = row.fromDate ? DateTime.fromISO(row.fromDate).startOf('day') : null;
    // Match covers(): untilDate is inclusive through end of that day.
    const until = row.untilDate ? DateTime.fromISO(row.untilDate).endOf('day') : null;
    if ((from && from > end) || (until && until < start)) continue;
    const overlapStart = from && from > start ? from : start;
    const overlapEnd = until && until < end ? until : end;
    if (overlapStart <= overlapEnd) {
      ranges.push({
        stationId: row.station.id,
        startDate: overlapStart,
        endDate: overlapEnd,
      });
    }
  }
  return ranges;
};

export const getStationInfoLabel = (car: Car, stationId: string, locale: string): string => {
  const row = car.stations.find((s) => s.station.id === stationId);
  const info = row?.station.info as Record<string, string> | undefined;
  return info?.[locale] ?? info?.de ?? stationId;
};

/** Primary affiliation rows used for "available at" grays, excluding periods linked on the line. */
export const availableAtBlocksForLine = (
  car: Car,
  lineStationId: string,
): { carStation: CarStations; startDate: DateTime | null; endDate: DateTime | null }[] => {
  const linkedHere = linkedIntervalsForStation(car, lineStationId);
  const result: {
    carStation: CarStations;
    startDate: DateTime | null;
    endDate: DateTime | null;
  }[] = [];
  for (const carStation of car.stations.filter(
    (s) => isPrimary(s) && s.station.id !== lineStationId,
  )) {
    const remnants = subtractIntervals(
      [toInterval(carStation.fromDate, carStation.untilDate)],
      linkedHere,
    );
    for (const remnant of remnants) {
      result.push({
        carStation,
        startDate: remnant.hideFrom ?? null,
        endDate: remnant.hideUntil ?? null,
      });
    }
  }
  return result;
};
